import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Sparkles, Timer, Target, Home, BarChart3, Settings, BookOpen, X, Globe, MessageSquare, Info, Share, Download, Calendar, Wand2, GraduationCap, Video, Lock, Shield, FileText, ChevronDown, Crown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AnimatedButton from '../ui/AnimatedButton';
import { FEATURE_FLAGS, GAMES_ENABLED } from '../../config/featureFlags';
import subscriptionService from '../../services/subscriptionService';

const Sidebar = ({
  onNavigate,
  onLogout,
  onClose,
  isMobile = false,
  onShareApp,
  onInstallApp,
  isInstallable,
  isStandalone,
  user,
  avatarUrl,
  isAdmin = false,
  onOpenPartyGame,
  onOpenTop10,
  onOpenFishbowl,
  onOpenPsych,
  onOpenDixit,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isMoreExpanded, setIsMoreExpanded] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);

  useEffect(() => {
    const loadSubscriptionStatus = async () => {
      if (user?.uid) {
        try {
          const status = await subscriptionService.getSubscriptionStatus(user.uid);
          setSubscriptionStatus(status);
        } catch (error) {
          console.error('Error loading subscription status:', error);
          setSubscriptionStatus(subscriptionService.getDefaultFreeStatus());
        } finally {
          setIsLoadingSubscription(false);
        }
      } else {
        setSubscriptionStatus(null);
        setIsLoadingSubscription(false);
      }
    };
    loadSubscriptionStatus();
  }, [user]);

  // Navigation items: first four = Dashboard, Exam Mocks, Lessons, Practice Hub
  const navItems = [
    {
      name: t('sidebar.dashboard'),
      path: '/dashboard',
      icon: <Home className="w-5 h-5" />,
      enabled: true
    },
    {
      name: t('sidebar.examMocks'),
      path: FEATURE_FLAGS.EXAM_SIMULATOR ? '/exam/' : '/coming-soon?feature=EXAM_SIMULATOR',
      icon: <Target className="w-5 h-5" />,
      enabled: FEATURE_FLAGS.EXAM_SIMULATOR,
      comingSoon: !FEATURE_FLAGS.EXAM_SIMULATOR
    },
    {
      name: t('sidebar.lessons'),
      path: FEATURE_FLAGS.LEARNING_PATH ? '/lessons' : '/coming-soon?feature=LEARNING_PATH',
      icon: <GraduationCap className="w-5 h-5" />,
      enabled: FEATURE_FLAGS.LEARNING_PATH,
      comingSoon: !FEATURE_FLAGS.LEARNING_PATH
    },
    {
      name: t('sidebar.practiceHub'),
      path: '/practice',
      icon: <BookOpen className="w-5 h-5" />,
      enabled: true
    },
    {
      name: t('dashboard.quickActions.viewStudyPlan'),
      path: FEATURE_FLAGS.STUDY_PLAN ? '/study-plan' : '/coming-soon?feature=STUDY_PLAN',
      icon: <Calendar className="w-5 h-5" />,
      enabled: FEATURE_FLAGS.STUDY_PLAN,
      comingSoon: !FEATURE_FLAGS.STUDY_PLAN
    },
    {
      name: t('sidebar.consultations'),
      path: '/consultations',
      icon: <Video className="w-5 h-5" />,
      enabled: true
    },
    {
      name: t('sidebar.blog'),
      path: '/blog',
      icon: <FileText className="w-5 h-5" />,
      enabled: true
    },
    ...(isAdmin ? [{
      name: t('sidebar.admin'),
      path: '/admin',
      icon: <Shield className="w-5 h-5" />,
      enabled: true
    }] : [])
  ];

  const PRIMARY_COUNT = 4;
  const primaryNav = navItems.slice(0, PRIMARY_COUNT);
  const moreNav = navItems.slice(PRIMARY_COUNT);

  // Path without locale prefix (e.g. /en/dashboard -> /dashboard) so active state matches
  const pathWithoutLang = location.pathname.replace(/^\/[a-z]{2}(?=\/|$)/, '') || '/';

  const isNavItemActive = (path) => {
    const basePath = path.split('?')[0].replace(/\/+$/, '');
    if (pathWithoutLang === basePath || pathWithoutLang.startsWith(basePath + '/')) return true;
    // Lessons, Practice: also active when under /exams/:examSlug/lessons or .../practice
    if (basePath === '/lessons') return pathWithoutLang.includes('/lessons');
    if (basePath === '/practice') return pathWithoutLang.includes('/practice');
    // Exam Mocks: active on /exam, /exam/..., or /exams/:slug (detail/simulator/instructions/results) but NOT on /exams/:slug/lessons or .../practice
    if (basePath === '/exam') {
      if (pathWithoutLang.startsWith('/exams/') && (pathWithoutLang.includes('/lessons') || pathWithoutLang.includes('/practice'))) return false;
      return pathWithoutLang.startsWith('/exams/');
    }
    return false;
  };

  const renderNavButton = (item) => {
    const isActive = isNavItemActive(item.path);
    return (
      <li key={item.path}>
        <button
          onClick={() => {
            if (!item.comingSoon && !item.isLocked) {
              onNavigate(item.path);
              if (isMobile) onClose();
            } else if (item.isLocked) {
              onNavigate('/pricing');
              if (isMobile) onClose();
            }
          }}
          style={{
            position: 'relative',
            width: '100%',
            overflow: 'hidden',
            ...(isActive ? {} : { backgroundColor: 'transparent' }),
            borderWidth: '2px',
            borderStyle: 'solid',
            borderColor: 'transparent',
            transition: 'all 0.3s ease'
          }}
          className={`touch-manipulation ${item.comingSoon || item.isLocked ? 'cursor-not-allowed' : 'cursor-pointer'} ${isActive ? 'bg-gray-100 rounded-xl text-black font-semibold' : 'hover:bg-gray-50 rounded-xl text-gray-600'}`}
          onMouseEnter={(e) => {
            if (!isActive && !item.comingSoon) {
              const navText = e.currentTarget.querySelector('.nav-text');
              const navIcon = e.currentTarget.querySelector('.nav-icon');
              const underline = e.currentTarget.querySelector('.nav-underline');
              if (navText) navText.style.color = '#000';
              if (navIcon) navIcon.style.color = '#000';
              if (underline) underline.style.width = '100%';
            }
          }}
          onMouseLeave={(e) => {
            if (!isActive && !item.comingSoon) {
              const navText = e.currentTarget.querySelector('.nav-text');
              const navIcon = e.currentTarget.querySelector('.nav-icon');
              const underline = e.currentTarget.querySelector('.nav-underline');
              if (navText) navText.style.color = '#374151';
              if (navIcon) navIcon.style.color = '#374151';
              if (underline) underline.style.width = '0';
            }
          }}
        >
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-3" style={{ position: 'relative', zIndex: 10 }}>
              <span
                className="nav-icon"
                style={{
                  color: isActive ? '#000' : (item.comingSoon || item.isLocked ? '#9CA3AF' : '#374151'),
                  transition: 'color 0.3s'
                }}
              >
                {item.isLocked ? <Lock className="w-5 h-5" /> : item.icon}
              </span>
              <span
                className="nav-text font-bold"
                style={{
                  color: isActive ? '#000' : (item.comingSoon || item.isLocked ? '#9CA3AF' : '#374151'),
                  transition: 'color 0.3s'
                }}
              >
                {item.name}
              </span>
            </div>
            {item.comingSoon && (
              <span className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-white bg-black rounded-full" style={{ position: 'relative', zIndex: 10 }}>
                <Sparkles className="w-3 h-3" />
                {t('sidebar.badge.soon')}
              </span>
            )}
            {item.isLocked && !item.comingSoon && (
              <span className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-white bg-black rounded-full" style={{ position: 'relative', zIndex: 10 }}>
                <Lock className="w-3 h-3" />
                {t('sidebar.badge.premium')}
              </span>
            )}
          </div>
          {!isActive && !item.comingSoon && !item.isLocked && (
            <span
              className="nav-underline"
              style={{
                position: 'absolute',
                left: '16px',
                bottom: '8px',
                width: '0',
                maxWidth: 'calc(100% - 32px)',
                height: '2px',
                backgroundColor: '#000',
                transition: 'width 0.3s ease',
                pointerEvents: 'none'
              }}
            />
          )}
        </button>
      </li>
    );
  };

  return (
    <aside className={`
      flex flex-col overflow-hidden bg-white
      ${isMobile ? 'w-full h-full border-none' : 'h-screen w-64 md:w-72 border-r border-slate-200'}
    `}>
      {/* Header: Logo only */}
      <div className="flex-shrink-0">
        <div className="flex items-center justify-between p-4 sm:p-6 mb-4">
          <button
            onClick={() => onNavigate('/')}
            className="flex items-center transition-transform hover:scale-105 active:scale-95 touch-manipulation min-h-[44px]"
          >
            <img
              src="/logo.png?v=3"
              alt="Logami Logo"
              className="h-10 w-10 sm:h-12 sm:w-12 object-contain scale-[3] origin-left"
            />
          </button>
          {isMobile && onClose && (
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation min-h-[44px] min-w-[44px]"
              aria-label="Close menu"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          )}
        </div>
      </div>

      {/* Scrollable navigation: main links, toggle, expanded list */}
      <div className="flex-1 overflow-y-scroll py-4 flex flex-col gap-1 hide-scrollbar min-h-0 px-4" style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', touchAction: 'pan-y' }}>
        <span className="block px-3 mb-2 text-xs font-semibold tracking-wider text-slate-400 uppercase">
          {t('sidebar.menuLabel', { defaultValue: 'Menu' })}
        </span>
        <ul className="space-y-1">
          {primaryNav.map((item) => renderNavButton(item))}
        </ul>

        {/* Expanded list: CSS grid animation, reliable on all browsers including iOS */}
        <div
          style={{
            display: 'grid',
            gridTemplateRows: isMoreExpanded ? '1fr' : '0fr',
            transition: 'grid-template-rows 0.3s ease',
          }}
          aria-hidden={!isMoreExpanded}
        >
          <div style={{ overflow: 'hidden' }}>
            <ul className="space-y-1 pt-1">
              {moreNav.map((item) => renderNavButton(item))}
            </ul>
            {isMobile && GAMES_ENABLED && (
            <div className="border-t border-gray-100 pt-3 mt-3">
              <h3 className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                🎮 {t('sidebar.games', { defaultValue: 'Games' })}
              </h3>
              <ul className="space-y-1 mb-3">
                <li>
                  <button
                    onClick={() => { onOpenPartyGame?.(); onClose?.(); }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-gray-600 hover:bg-gray-50 font-bold transition-colors touch-manipulation"
                  >
                    <span className="text-lg">🗣️</span>
                    <span>Articulate</span>
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => { onOpenTop10?.(); onClose?.(); }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-gray-600 hover:bg-gray-50 font-bold transition-colors touch-manipulation"
                  >
                    <span className="text-lg">🔟</span>
                    <span>Top 10</span>
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => { onOpenFishbowl?.(); onClose?.(); }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-gray-600 hover:bg-gray-50 font-bold transition-colors touch-manipulation"
                  >
                    <span className="text-lg">🐟</span>
                    <span>Fishbowl</span>
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => { onOpenPsych?.(); onClose?.(); }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-gray-600 hover:bg-gray-50 font-bold transition-colors touch-manipulation"
                  >
                    <span className="text-lg">🧠</span>
                    <span>Psych!</span>
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => { onOpenDixit?.(); onClose?.(); }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-gray-600 hover:bg-gray-50 font-bold transition-colors touch-manipulation"
                  >
                    <span className="text-lg">🃏</span>
                    <span>Dixit</span>
                  </button>
                </li>
              </ul>
            </div>
            )}
            <div className="border-t border-gray-100 pt-3 mt-3">
              <h3 className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                {t('sidebar.appFeatures')}
              </h3>
              <ul className="space-y-1">
                {isInstallable && !isStandalone && (
                  <li>
                    <button
                      onClick={onInstallApp}
                      style={{ position: 'relative', overflow: 'hidden' }}
                      className="group w-full flex items-center p-3 rounded-lg text-gray-600 hover:bg-black hover:text-white transition-colors touch-manipulation min-h-[48px]"
                      onMouseEnter={(e) => {
                        const underline = e.currentTarget.querySelector('.app-feature-underline');
                        if (underline) underline.style.width = '100%';
                      }}
                      onMouseLeave={(e) => {
                        const underline = e.currentTarget.querySelector('.app-feature-underline');
                        if (underline) underline.style.width = '0';
                      }}
                    >
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center mr-3 bg-gray-100 text-gray-600 group-hover:bg-white group-hover:text-black transition-colors">
                        <Download className="w-5 h-5" />
                      </div>
                      <span className="font-medium text-base">{t('sidebar.installApp')}</span>
                      <span
                        className="app-feature-underline"
                        style={{
                          position: 'absolute',
                          left: '16px',
                          bottom: '8px',
                          width: '0',
                          maxWidth: 'calc(100% - 32px)',
                          height: '2px',
                          backgroundColor: '#000',
                          transition: 'width 0.3s ease',
                          pointerEvents: 'none'
                        }}
                      />
                    </button>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* More / Show less, always at bottom of list, full width */}
        <button
          type="button"
          onClick={() => setIsMoreExpanded((prev) => !prev)}
          className="w-full flex items-center justify-center gap-2 py-2 text-sm text-slate-500 hover:text-slate-800 font-medium transition-colors touch-manipulation"
          aria-expanded={isMoreExpanded}
          aria-label={isMoreExpanded ? t('sidebar.showLess') : t('sidebar.more')}
        >
          <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${isMoreExpanded ? 'rotate-180' : ''}`} />
          <span>
            {isMoreExpanded ? t('sidebar.showLess') : t('sidebar.more')}
          </span>
        </button>
      </div>

      {/* Fixed footer: Account zone. Upgrade CTA (free only), profile (with Pro badge when premium), Settings */}
      <div className="mt-auto p-4 border-t border-slate-100 flex-shrink-0 flex flex-col gap-0">
        {!isLoadingSubscription && subscriptionStatus != null && subscriptionStatus.isFree && (
          <button
            type="button"
            onClick={() => { onNavigate('/pricing'); if (isMobile) onClose?.(); }}
            className="w-full mb-3 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-white font-medium bg-gradient-to-r from-violet-600 via-purple-500 to-pink-500 shadow-sm hover:opacity-90 transition-opacity touch-manipulation"
          >
            <Crown className="w-4 h-4 flex-shrink-0" />
            {t('sidebar.upgradeToPremium', { defaultValue: 'Upgrade to Premium' })}
          </button>
        )}
        {user && (
          <button
            type="button"
            onClick={() => {
              onNavigate('/profile');
              if (isMobile) onClose?.();
            }}
            className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer mb-1 transition-colors ${(pathWithoutLang === '/profile' || pathWithoutLang.startsWith('/profile/')) ? 'bg-gray-100 text-black font-semibold' : 'hover:bg-gray-50 text-gray-600'}`}
            aria-label={t('sidebar.profile')}
          >
            <img
              src={avatarUrl}
              alt=""
              className="w-6 h-6 rounded-full border border-slate-200 object-cover flex-shrink-0"
            />
            <div className="flex-1 min-w-0 flex items-center flex-wrap gap-1">
              <span className="font-medium text-sm text-slate-800 truncate">
                {user.displayName || user.email?.split('@')[0]}
              </span>
              {user.email && user.emailVerified === false && (
                <span className="px-1.5 py-0.5 rounded-md bg-amber-100 text-[10px] font-bold text-amber-700 uppercase tracking-wider flex-shrink-0">
                  {t('sidebar.emailVerification.notVerified')}
                </span>
              )}
              {!isLoadingSubscription && subscriptionStatus != null && !subscriptionStatus.isFree && (
                <span className="ml-2 px-1.5 py-0.5 rounded-md bg-gradient-to-r from-violet-600 via-purple-500 to-pink-500 text-[10px] font-bold text-white uppercase tracking-wider shadow-sm flex-shrink-0">
                  {t('sidebar.badge.pro')}
                </span>
              )}
            </div>
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            onNavigate('/settings');
            if (isMobile) onClose?.();
          }}
          className={`flex items-center gap-3 p-3 rounded-xl transition-colors font-medium ${pathWithoutLang === '/settings' ? 'bg-gray-100 text-black font-semibold' : 'text-gray-600 hover:bg-gray-50 hover:text-black'}`}
        >
          <Settings className="w-5 h-5 flex-shrink-0" />
          <span>{t('sidebar.settings')}</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;